import type { RequestOptions } from "@@/plugin-request/request";
import type { RequestConfig } from "@umijs/max";
import { message } from "antd";
import type { ApiResponse, ErrorInfo } from "./types/response";
import { ResponseState } from "./types/response";
import {
  getHttpStatusFallbackMessage,
  getRequestErrorMessage,
} from "./utils/requestError";

// 使用统一的响应结构类型
type ResponseStructure = ApiResponse;

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { data, message, state } = res as unknown as ResponseStructure;
      if (state !== ResponseState.SUCCESS) {
        const error: any = new Error(message);
        error.name = "BizError";
        error.info = { message, state, data } as ErrorInfo;
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === "BizError") {
        const errorInfo: ErrorInfo = error.info;
        if (errorInfo) {
          const { message: errorMessage, state } = errorInfo;
          // 根据状态显示不同的错误信息
          switch (state) {
            case ResponseState.ERROR:
              message.error(errorMessage);
              break;
            case ResponseState.WARNING:
              message.warning(errorMessage);
              break;
            case ResponseState.INFO:
              message.info(errorMessage);
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        const status = error.response?.status;
        if (status === 401) {
          message.error("登录已过期，请重新登录");
          // 401未授权，清除token并跳转到登录页
          sessionStorage.removeItem("admin_token");
          if (window.location.pathname !== "/user/login") {
            window.location.href = "/user/login";
          }
          return;
        }

        const errorMessage = getRequestErrorMessage(
          error,
          getHttpStatusFallbackMessage(status)
        );
        message.error(errorMessage);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error("网络无响应，请检查网络后重试");
      } else {
        // 发送请求时出了点问题
        message.error(getRequestErrorMessage(error, "请求失败，请稍后重试"));
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 添加Bearer token认证
      const token = sessionStorage.getItem("admin_token");
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return config;
    },
  ],
  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { message: responseMessage, state } =
        response as unknown as ResponseStructure;

      // 如果状态不是成功，显示错误信息
      if (state !== ResponseState.SUCCESS) {
        message.error(responseMessage || "请求失败！");
      }

      // 返回完整的响应数据
      return response;
    },
  ],
};
